import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  FolderOpen,
  Briefcase,
  Clock,
  Edit,
  Plus,
  CheckCircle,
  FileText,
  GripVertical
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { projectService, caseService, taskService } from '../../services/api';
import DocumentUpload from '../../components/documents/DocumentUpload';
import DocumentList from '../../components/documents/DocumentList';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';
import Breadcrumb from '../../components/common/Breadcrumb';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import UserSelectDropdown from '../../components/common/UserSelectDropdown';

const SortableTaskCard = ({ task, onClick, getStatusColor }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-blue-300 cursor-pointer transition-colors flex items-center gap-3"
    >
      <button
        type="button"
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="flex-1 min-w-0" onClick={onClick}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">{task.title}</h3>
            <p className="text-sm text-gray-600">{task.description}</p>
          </div>
          <div className="text-right">
            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
              {task.status}
            </span>
            {task.due_date && (
              <p className="text-xs text-gray-500 mt-1 flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                Due {new Date(task.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const INITIAL_TASK_FORM = {
  name: '',
  description: '',
  status: 'todo',
  priority: 'medium',
  estimated_hours: '',
  due_date: '',
  assigned_to: '',
  task_type: 'research'
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { notification, showSuccess, showError, showWarning, hideNotification } = useNotification();
  const [project, setProject] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentsKey, setDocumentsKey] = useState(0); // For refreshing document list
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  // Inline edit states
  const [editingField, setEditingField] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState('');

  // Form states
  const [editForm, setEditForm] = useState({});
  const [taskForm, setTaskForm] = useState({
    ...INITIAL_TASK_FORM
  });

  // Drag-and-drop sensors -- require a small drag distance to avoid
  // accidental reorders when clicking tasks.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tasks.findIndex((t) => t.id === active.id);
    const newIndex = tasks.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(tasks, oldIndex, newIndex);
    setTasks(reordered);

    // Persist new order to the server
    const orderPayload = reordered.map((t, idx) => ({
      id: t.id,
      order_index: idx,
    }));

    try {
      await taskService.reorderTasks(orderPayload);
    } catch (err) {
      console.error('Failed to persist task order:', err);
      showError('Failed to save task order');
      // Revert on failure
      setTasks(tasks);
    }
  }, [tasks, showError]);

  const fetchProjectData = useCallback(async () => {
    try {
      const projectResponse = await projectService.getProject(id);
      setProject(projectResponse.data);

      // Fetch related case data
      if (projectResponse.data.case_id) {
        const caseResponse = await caseService.getCase(projectResponse.data.case_id);
        setCaseData(caseResponse.data);
        // Store context for Tools page
        localStorage.setItem('lastViewedCase', JSON.stringify({
          caseId: projectResponse.data.case_id,
          caseTitle: caseResponse.data.title,
          caseNumber: caseResponse.data.case_number,
        }));
        localStorage.setItem('lastViewedProject', JSON.stringify({
          projectId: id,
          projectName: projectResponse.data.name,
        }));
      }

      // Fetch tasks for this project
      try {
        const tasksResponse = await taskService.getTasksByProject(id);
        // Backend returns {"tasks": [...]}, so we need to access the tasks property
        setTasks(tasksResponse.data?.tasks || []);
      } catch (taskError) {
        console.error('Error fetching tasks:', taskError);
        // Don't fail the whole page if tasks can't be fetched
        setTasks([]);
      }

    } catch (error) {
      console.error('Error fetching project:', error);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Handle quick-action openAction state from navbar
  useEffect(() => {
    const openAction = location.state?.openAction;
    if (openAction === 'new-task' && !loading) {
      setShowTaskModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, loading, navigate, location.pathname]);

  const getStatusColor = (taskStatus) => {
    switch (taskStatus) {
      case 'planned':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on_hold':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const handleDocumentUpload = () => {
    // Refresh documents list
    setDocumentsKey(prev => prev + 1);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!project || updatingStatus) return;

    setUpdatingStatus(true);
    try {
      const response = await projectService.updateProjectStatus(project.id, {
        status: newStatus,
        comment: `Status updated to ${newStatus}`
      });

      setProject(response.data);
      console.log('Project status updated successfully');
    } catch (err) {
      console.error('Error updating project status:', err);
      setError(err.response?.data?.detail || 'Failed to update project status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleEditProject = () => {
    if (project) {
      setEditForm({
        name: project.name || '',
        description: project.description || '',
        estimated_value: project.estimated_value || '',
        estimated_hours: project.estimated_hours || '',
        status: project.status || 'planned',
        priority: project.priority || 'medium',
        start_date: project.start_date ? project.start_date.split('T')[0] : '',
        due_date: project.due_date ? project.due_date.split('T')[0] : '',
        requires_payment_first: project.requires_payment_first !== false
      });
      setShowEditModal(true);
    }
  };

  const handleSaveProject = async () => {
    try {
      const updateData = {
        ...editForm,
        estimated_value: editForm.estimated_value ? parseFloat(editForm.estimated_value) : null,
        estimated_hours: editForm.estimated_hours ? parseFloat(editForm.estimated_hours) : null,
        start_date: editForm.start_date || null,
        due_date: editForm.due_date || null
      };

      // Remove empty string fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '') {
          updateData[key] = null;
        }
      });

      await projectService.updateProject(id, updateData);
      setShowEditModal(false);
      await fetchProjectData();
      showSuccess('Project updated successfully!');
    } catch (err) {
      console.error('Error updating project:', err);
      showError(`Failed to update project: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleCreateTask = async (keepOpen = false) => {
    try {
      if (!taskForm.name) {
        showWarning('Task name is required');
        return;
      }

      const taskData = {
        project_id: id,
        title: taskForm.name, // Backend expects 'title', not 'name'
        description: taskForm.description || null,
        status: taskForm.status || 'todo', // Backend default is 'todo', not 'not_started'
        priority: taskForm.priority || 'medium',
        task_type: taskForm.task_type || 'other',
        estimated_hours: taskForm.estimated_hours ? parseFloat(taskForm.estimated_hours) : null,
        due_date: taskForm.due_date || null,
        assigned_to: taskForm.assigned_to || null,
        billable: true // Backend expects this field
      };

      // Remove empty string fields
      Object.keys(taskData).forEach(key => {
        if (taskData[key] === '') {
          taskData[key] = null;
        }
      });

      await taskService.createTask(taskData);
      setTaskForm({ ...INITIAL_TASK_FORM });
      if (!keepOpen) {
        setShowTaskModal(false);
      }
      // Refresh project data to get updated tasks
      await fetchProjectData();
      showSuccess('Task created successfully!');
    } catch (err) {
      console.error('Error creating task:', err);
      // Show more detailed error information
      let errorMessage = 'Failed to create task';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Handle validation errors
          const validationErrors = err.response.data.detail.map(e =>
            `${e.loc?.join('.')}: ${e.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      showError(errorMessage);
    }
  };

  const confirmMarkComplete = async () => {
    await handleStatusUpdate('completed');
    setShowCompleteConfirm(false);
  };

  const startInlineEdit = (field, currentValue) => {
    setEditingField(field);
    setInlineEditValue(currentValue || '');
  };

  const cancelInlineEdit = () => {
    setEditingField(null);
    setInlineEditValue('');
  };

  const saveInlineEdit = async (field) => {
    // Don't save if we've already cancelled (e.g., blur after Escape)
    if (editingField !== field) return;

    try {
      let updateData = {};

      if (field === 'due_date') {
        updateData.due_date = inlineEditValue || null;
      } else if (field === 'priority') {
        updateData.priority = inlineEditValue;
      } else if (field === 'description') {
        updateData.description = inlineEditValue || null;
      }

      cancelInlineEdit();
      await projectService.updateProject(id, updateData);
      await fetchProjectData();
      showSuccess(`${field.replace('_', ' ')} updated successfully!`);
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      showError(`Failed to update ${field}: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleInlineKeyDown = (e, field) => {
    if (e.key === 'Enter' && field !== 'description') {
      e.preventDefault();
      saveInlineEdit(field);
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <FolderOpen className="h-16 w-16 mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Project Not Found</h2>
        <p className="text-gray-600 mb-4">{error || 'The requested project could not be found.'}</p>
        <Link
          to="/cases"
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
        >
          Back to Cases
        </Link>
      </div>
    );
  }

  const breadcrumbItems = caseData
    ? [{ label: `${caseData.case_number} - ${caseData.title}`, icon: Briefcase, to: `/cases/${caseData.id}` }]
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <FolderOpen className="mr-3 h-8 w-8" />
                {project.name}
              </h1>
              {editingField === 'status' ? (
                <select
                  value={inlineEditValue}
                  onChange={(e) => {
                    setInlineEditValue(e.target.value);
                    handleStatusUpdate(e.target.value);
                    cancelInlineEdit();
                  }}
                  onBlur={cancelInlineEdit}
                  disabled={updatingStatus}
                  className="text-sm border border-yellow-400 rounded-full px-3 py-1 bg-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:opacity-50"
                  autoFocus
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              ) : (
                <span
                  onClick={() => startInlineEdit('status', project.status)}
                  className={`px-3 py-1 text-sm rounded-full cursor-pointer hover:ring-2 hover:ring-yellow-400 transition-all ${getStatusColor(project.status)}`}
                  title="Click to change status"
                >
                  {project.status.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={handleEditProject}
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
        >
          <Edit className="mr-2 h-4 w-4" />
          Edit Project
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Overview */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Project Overview</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Created</label>
                <p className="text-gray-900">
                  {new Date(project.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Updated</label>
                <p className="text-gray-900">
                  {new Date(project.updated_at).toLocaleDateString()}
                </p>
              </div>

              {/* Inline Editable Due Date */}
              <div>
                <label className="text-sm font-medium text-gray-500">Due Date</label>
                {editingField === 'due_date' ? (
                  <div className="mt-1">
                    <input
                      type="date"
                      value={inlineEditValue}
                      onChange={(e) => {
                        setInlineEditValue(e.target.value);
                        // Auto-save on change for date picker
                      }}
                      onBlur={() => saveInlineEdit('due_date')}
                      onKeyDown={(e) => handleInlineKeyDown(e, 'due_date')}
                      className="border border-yellow-400 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-yellow-50"
                      autoFocus
                    />
                  </div>
                ) : (
                  <p
                    onClick={() => startInlineEdit('due_date', project.due_date ? project.due_date.split('T')[0] : '')}
                    className="text-gray-900 cursor-pointer hover:bg-yellow-50 hover:text-yellow-700 px-2 py-1 rounded -ml-2 transition-colors"
                    title="Click to edit"
                  >
                    {project.due_date ? new Date(project.due_date).toLocaleDateString() : <span className="text-gray-400 italic">Click to set</span>}
                  </p>
                )}
              </div>

              {/* Inline Editable Priority */}
              <div>
                <label className="text-sm font-medium text-gray-500">Priority</label>
                {editingField === 'priority' ? (
                  <div className="mt-1">
                    <select
                      value={inlineEditValue}
                      onChange={(e) => {
                        setInlineEditValue(e.target.value);
                        // Will save on blur
                      }}
                      onBlur={() => saveInlineEdit('priority')}
                      onKeyDown={(e) => handleInlineKeyDown(e, 'priority')}
                      className="border border-yellow-400 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-yellow-50"
                      autoFocus
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                ) : (
                  <div
                    onClick={() => startInlineEdit('priority', project.priority || 'medium')}
                    className="cursor-pointer hover:ring-2 hover:ring-yellow-400 rounded-full inline-block transition-all"
                    title="Click to edit"
                  >
                    <span className={`inline-block px-2 py-1 text-xs rounded-full border ${getPriorityColor(project.priority)}`}>
                      {(project.priority || 'medium').toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Inline Editable Description */}
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-500">Description</label>
              {editingField === 'description' ? (
                <div className="mt-1">
                  <textarea
                    value={inlineEditValue}
                    onChange={(e) => setInlineEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelInlineEdit();
                    }}
                    rows={3}
                    className="w-full border border-yellow-400 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-yellow-50"
                    autoFocus
                    placeholder="Enter project description..."
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <button
                      onClick={() => saveInlineEdit('description')}
                      className="bg-yellow-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-yellow-600 transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelInlineEdit}
                      className="text-gray-600 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-100 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  onClick={() => startInlineEdit('description', project.description || '')}
                  className="text-gray-900 mt-1 cursor-pointer hover:bg-yellow-50 hover:text-yellow-700 px-2 py-1 rounded -ml-2 transition-colors min-h-[2rem]"
                  title="Click to edit"
                >
                  {project.description || <span className="text-gray-400 italic">Click to add description</span>}
                </p>
              )}
            </div>
          </div>

          {/* Tasks */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Tasks</h2>
              <button
                onClick={() => setShowTaskModal(true)}
                className="bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center hover:bg-green-700"
              >
                <Plus className="mr-1 h-4 w-4" />
                New Task
              </button>
            </div>

            {tasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600">No tasks yet</p>
                <p className="text-gray-500 text-sm">Create your first task to track progress</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={tasks.map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <SortableTaskCard
                        key={task.id}
                        task={task}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        getStatusColor={getStatusColor}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Documents Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                Project Documents
              </h2>
            </div>

            {/* Document Upload */}
            <div className="mb-6">
              <DocumentUpload
                caseId={caseData?.id}
                projectId={id}
                onUploadSuccess={handleDocumentUpload}
                multiple={true}
                className="mb-4"
              />
            </div>

            {/* Document List */}
            <DocumentList
              key={documentsKey}
              caseId={caseData?.id}
              projectId={id}
              limit={20}
              showActions={true}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Project Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Project Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Tasks</span>
                <span className="font-medium">{tasks.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Completed</span>
                <span className="font-medium text-green-600">
                  {tasks.filter(t => t.status === 'completed').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">In Progress</span>
                <span className="font-medium text-yellow-600">
                  {tasks.filter(t => t.status === 'in_progress').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Overdue</span>
                <span className="font-medium text-red-600">
                  {tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed').length}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Project</h3>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Value</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.estimated_value}
                    onChange={(e) => setEditForm({...editForm, estimated_value: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={editForm.estimated_hours}
                    onChange={(e) => setEditForm({...editForm, estimated_hours: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={editForm.priority}
                    onChange={(e) => setEditForm({...editForm, priority: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={editForm.start_date}
                    onChange={(e) => setEditForm({...editForm, start_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.requires_payment_first}
                  onChange={(e) => setEditForm({...editForm, requires_payment_first: e.target.checked})}
                  className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 mr-2"
                />
                <label className="text-sm text-gray-700">Requires Payment First</label>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSaveProject}
                className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New Task</h3>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Name *</label>
                <input
                  type="text"
                  value={taskForm.name}
                  onChange={(e) => setTaskForm({...taskForm, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Enter task name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Task description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                  <select
                    value={taskForm.task_type}
                    onChange={(e) => setTaskForm({...taskForm, task_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="research">Research</option>
                    <option value="drafting">Drafting</option>
                    <option value="filing">Filing</option>
                    <option value="meeting">Meeting</option>
                    <option value="court_appearance">Court Appearance</option>
                    <option value="client_communication">Client Communication</option>
                    <option value="discovery">Discovery</option>
                    <option value="review">Review</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({...taskForm, priority: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={taskForm.status}
                    onChange={(e) => setTaskForm({...taskForm, status: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={taskForm.estimated_hours}
                    onChange={(e) => setTaskForm({...taskForm, estimated_hours: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <UserSelectDropdown
                  value={taskForm.assigned_to}
                  onChange={(userId) => setTaskForm({...taskForm, assigned_to: userId || ''})}
                  placeholder="Search for a team member..."
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => handleCreateTask(false)}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Create Task
              </button>
              <button
                onClick={() => handleCreateTask(true)}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Save & Create Another
              </button>
              <button
                onClick={() => setShowTaskModal(false)}
                className="flex-shrink-0 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Complete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={confirmMarkComplete}
        title="Mark Project Complete"
        message="Are you sure you want to mark this project as completed?"
        confirmText="Mark Complete"
        cancelText="Cancel"
        variant="success"
      />

      {/* Notification Toast */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default ProjectDetail;
